import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  Loader2,
  Plus,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { GetEventsAPI, type StudentEvent } from "@/services/api/user/events";
import { SearchEventsAPI } from "@/services/api/search/searchEvents";
import { GetUserSocietiesAPI } from "@/services/api/society/getUserSocieties";
import {
  GetPublicSocietiesAPI,
  type PublicSociety,
} from "@/services/api/society/getPublicSocieties";
import { universityList } from "@/assets/universityList";
import { EVENT_DATE_PRESETS } from "@/utils/eventDateFilter";
import {
  POST_TYPE_BADGE_CLASS,
  POST_TYPE_LABELS,
  postTypeLabelKey,
} from "@/utils/eventFormat";
import StudentEventCard from "@/components/student/StudentEventCard";
import AdminEmptyState from "@/components/AdminEmptyState";
import AdminListSkeleton from "@/components/AdminListSkeleton";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";

const PAGE_SIZE = 10;

const SEARCH_DEBOUNCE_MS = 300;

const TOOLBAR_SCROLL_DELTA = 8;

const TOOLBAR_HIDE_OFFSET = 90;

const ALL = "all";

const FILTER_SELECT_CLASS =
  "w-full appearance-none rounded-xl border border-gray-200 bg-white px-3 py-2 pr-8 text-sm text-gray-700 outline-none transition focus:border-[#ffa217] focus:ring-2 focus:ring-[#ffa217]/30";

const PostTypeChip: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`cursor-pointer select-none transition ${
      active
        ? "inline-flex items-center rounded-full border border-[#ffa217] bg-[#ffa217] px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm hover:bg-[#f59200]"
        : `${POST_TYPE_BADGE_CLASS} hover:bg-[#ffe7c2]`
    }`}
  >
    {active && (
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="mr-1"
      >
        <path d="M20 6 9 17l-5-5" />
      </svg>
    )}
    {label}
  </button>
);

/**
 * Student home — the event feed, laid out like the RN `HomeScreen` and
 * `EventBox`: a single Instagram-style column of cards, newest first, ten per
 * page, auto-loading the next page on scroll and refreshed with a
 * pull-to-refresh gesture. Society members additionally get the floating
 * create-event button, and their own events open straight into the edit wizard.
 * The feed is controlled by one compact toolbar: a search box that queries the
 * search endpoint as you type (debounced, and committable early with Enter),
 * and a filters button that opens a modal for post type, university and
 * society. Filters are only committed on "Apply".
 */
const StudentHomePage: React.FC = () => {
  const { t } = useTranslation();
  const authSocietyIds = useSelector(
    (state: any) => state.auth?.student?.society_id,
  );

  const [events, setEvents] = useState<StudentEvent[]>([]);
  const [societyIds, setSocietyIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filtering, setFiltering] = useState(false);
  const [error, setError] = useState(false);

  // Applied filters — what the feed currently reflects.
  const [postType, setPostType] = useState(ALL);
  const [university, setUniversity] = useState(ALL);
  const [society, setSociety] = useState(ALL);
  const [datePreset, setDatePreset] = useState("any");
  const [openOnly, setOpenOnly] = useState(false);
  const [location, setLocation] = useState("");
  const [hashtag, setHashtag] = useState("");
  const [societies, setSocieties] = useState<PublicSociety[]>([]);

  // Draft filters — edited inside the modal until "Apply" is pressed.
  const [draftPostType, setDraftPostType] = useState(ALL);
  const [draftUniversity, setDraftUniversity] = useState(ALL);
  const [draftSociety, setDraftSociety] = useState(ALL);
  const [draftDatePreset, setDraftDatePreset] = useState("any");
  const [draftOpenOnly, setDraftOpenOnly] = useState(false);
  const [draftLocation, setDraftLocation] = useState("");
  const [draftHashtag, setDraftHashtag] = useState("");
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // Live search text vs. the committed query that triggers a fetch.
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const searchTimerRef = useRef<number | null>(null);

  // Toolbar auto-hide — tracks the last scroll position and whether the
  // search box currently holds focus, so the toolbar only slides away while
  // the user is actively browsing the feed.
  const [toolbarHidden, setToolbarHidden] = useState(false);
  const lastScrollYRef = useRef(0);
  const searchFocusedRef = useRef(false);

  // Identifies the newest page request, so a slow response that was already in
  // flight cannot overwrite the results of a newer one.
  const fetchIdRef = useRef(0);

  const activeFilterCount = [
    postType !== ALL,
    university !== ALL,
    society !== ALL,
    datePreset !== "any",
    openOnly,
    location.trim().length > 0,
    hashtag.trim().length > 0,
  ].filter(Boolean).length;
  const hasActiveFilter = activeFilterCount > 0;
  const isSearching = searchQuery.length > 0;

  const isSocietyMember = societyIds.length > 0;
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const postTypes = useMemo(() => Object.keys(POST_TYPE_LABELS), []);

  const scopedSocieties = useMemo(
    () =>
      draftUniversity === ALL
        ? societies
        : societies.filter((s) => s.university === draftUniversity),
    [societies, draftUniversity],
  );

  const fetchPage = useCallback(
    async (targetPage: number, mode: "initial" | "more" | "refresh") => {
      const fetchId = ++fetchIdRef.current;
      if (mode === "more") setLoadingMore(true);
      const filters = {
        ...(postType !== ALL ? { postType: [postType] } : {}),
        ...(university !== ALL ? { university: [university] } : {}),
        ...(society !== ALL ? { society: [{ id: society }] } : {}),
        ...(datePreset !== "any" ? { datePreset } : {}),
        ...(openOnly ? { openOnly: true } : {}),
        ...(location.trim() ? { location: location.trim() } : {}),
        ...(hashtag.trim() ? { hashtag: hashtag.trim() } : {}),
      };
      // A committed search query swaps the plain feed for the search endpoint;
      // the same filters ride along so search + filters combine.
      const resp = isSearching
        ? await SearchEventsAPI({
            searchText: searchQuery,
            page: targetPage,
            ...filters,
          })
        : await GetEventsAPI({ page: targetPage, ...filters });
      if (mode === "more") setLoadingMore(false);
      if (mode === "initial") setFiltering(false);
      setLoading(false);

      // A newer request (a newer search query, filter or page) started while
      // this one was in flight — drop this response instead of overwriting it.
      if (fetchId !== fetchIdRef.current) return;

      const result = resp?.result;
      if (resp?.status !== 0 || !Array.isArray(result)) {
        setError(true);
        setHasMore(false);
        return;
      }

      setError(false);
      setPage(targetPage);
      setHasMore(result.length >= PAGE_SIZE);
      setEvents((prev) => (mode === "more" ? [...prev, ...result] : result));
    },
    [
      isSearching,
      postType,
      searchQuery,
      society,
      university,
      datePreset,
      openOnly,
      location,
      hashtag,
    ],
  );

  const { refreshing, touchProps } = usePullToRefresh(() =>
    fetchPage(1, "refresh"),
  );

  useEffect(() => {
    fetchPage(1, "initial");
  }, [fetchPage]);

  const cancelPendingSearch = () => {
    if (searchTimerRef.current === null) return;
    window.clearTimeout(searchTimerRef.current);
    searchTimerRef.current = null;
  };

  // Auto-search: commit the live input once typing pauses, so the search
  // endpoint is hit once per pause instead of once per keystroke. Nothing is
  // scheduled while the input already matches the committed query.
  useEffect(() => {
    const query = searchText.trim();
    if (query === searchQuery) return;
    searchTimerRef.current = window.setTimeout(() => {
      searchTimerRef.current = null;
      setFiltering(true);
      setSearchQuery(query);
    }, SEARCH_DEBOUNCE_MS);
    return () => cancelPendingSearch();
  }, [searchText, searchQuery]);

  useEffect(() => {
    GetPublicSocietiesAPI().then(setSocieties);
  }, []);

  useEffect(() => {
    if (filterModalOpen) {
      // Seed the drafts with the applied values every time the modal opens.
      setDraftPostType(postType);
      setDraftUniversity(university);
      setDraftSociety(society);
      setDraftDatePreset(datePreset);
      setDraftOpenOnly(openOnly);
      setDraftLocation(location);
      setDraftHashtag(hashtag);
    }
  }, [
    filterModalOpen,
    postType,
    society,
    university,
    datePreset,
    openOnly,
    location,
    hashtag,
  ]);

  useEffect(() => {
    if (!filterModalOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFilterModalOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [filterModalOpen]);

  useEffect(() => {
    // A society outside the selected university's scope is no longer valid.
    if (
      draftSociety !== ALL &&
      !scopedSocieties.some((s) => s.id === draftSociety)
    ) {
      setDraftSociety(ALL);
    }
  }, [scopedSocieties, draftSociety]);

  useEffect(() => {
    if (filterModalOpen) setToolbarHidden(false);
  }, [filterModalOpen]);

  useEffect(() => {
    // The toolbar rides on the window scroll (it is sticky, not inside a
    // scroll container), so hide it while scrolling down through the feed and
    // bring it back on any upward scroll. Focusing the search box or opening
    // the filters always keeps it in place.
    const onScroll = () => {
      const current = window.scrollY;
      const previous = lastScrollYRef.current;
      lastScrollYRef.current = current;
      if (Math.abs(current - previous) < TOOLBAR_SCROLL_DELTA) return;
      if (searchFocusedRef.current || filterModalOpen) return;
      if (current > previous) {
        if (current > TOOLBAR_HIDE_OFFSET) setToolbarHidden(true);
      } else {
        setToolbarHidden(false);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [filterModalOpen]);

  useEffect(() => {
    GetUserSocietiesAPI().then((societies) => {
      const ids = societies
        .map((society) => society.id)
        .filter((id): id is string => typeof id === "string" && id.length > 0);
      if (ids.length > 0) {
        setSocietyIds(ids);
        return;
      }
      setSocietyIds(
        Array.isArray(authSocietyIds)
          ? authSocietyIds.map((id: unknown) => String(id))
          : [],
      );
    });
  }, [authSocietyIds]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const maybeLoadMore = () => {
      if (hasMore && !loadingMore && !loading) {
        fetchPage(page + 1, "more");
      }
    };

    if (typeof IntersectionObserver === "undefined") {
      const onScroll = () => {
        const reachedBottom =
          window.innerHeight + window.scrollY >=
          document.documentElement.scrollHeight - 400;
        if (reachedBottom) maybeLoadMore();
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => window.removeEventListener("scroll", onScroll);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) maybeLoadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchPage, hasMore, loading, loadingMore, page]);

  const handleSubscribedChange = (eventId: string, subscribed: boolean) => {
    setEvents((prev) =>
      prev.map((item) =>
        item.details.id === eventId
          ? { ...item, subscribed, organizers: item.organizers }
          : item,
      ),
    );
  };

  const handleFollowChange = (
    eventId: string,
    organizerId: string,
    following: boolean,
  ) => {
    setEvents((prev) =>
      prev.map((item) =>
        item.details.id === eventId
          ? {
              ...item,
              organizers: (item.organizers ?? []).map((organizer) =>
                organizer.id === organizerId
                  ? { ...organizer, following }
                  : organizer,
              ),
            }
          : item,
      ),
    );
  };

  const commitSearch = (text: string) => {
    cancelPendingSearch();
    setSearchText(text);
    const query = text.trim();
    if (query === searchQuery) return;
    setFiltering(true);
    setSearchQuery(query);
  };

  const applyFilters = () => {
    setFilterModalOpen(false);
    const changed =
      draftPostType !== postType ||
      draftUniversity !== university ||
      draftSociety !== society ||
      draftDatePreset !== datePreset ||
      draftOpenOnly !== openOnly ||
      draftLocation.trim() !== location.trim() ||
      draftHashtag.trim().replace(/^#/, "") !==
        hashtag.trim().replace(/^#/, "");
    if (!changed) return;
    setFiltering(true);
    setPostType(draftPostType);
    setUniversity(draftUniversity);
    setSociety(draftSociety);
    setDatePreset(draftDatePreset);
    setOpenOnly(draftOpenOnly);
    setLocation(draftLocation.trim());
    setHashtag(draftHashtag.trim().replace(/^#/, ""));
  };

  return (
    <div className="mx-auto max-w-[600px] px-4 py-6" {...touchProps}>
      {/* Toolbar: search box + filters button, one compact row, stuck under the
          layout header (h-16) so the search stays reachable while scrolling.
          Sized to the content column (like the event cards) instead of
          bleeding into the page gutter. It slides out of the way on downward
          scroll (transform only, so the sticky box keeps its place in the
          flow) and slides back on any upward scroll. */}
      <div
        className={`sticky top-16 z-30 mb-3 will-change-transform rounded-2xl border border-gray-100 bg-white/95 px-3 py-3 shadow-sm backdrop-blur transition-transform duration-300 ease-in-out ${
          toolbarHidden ? "-translate-y-[calc(100%+1rem)]" : "translate-y-0"
        }`}
      >
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              inputMode="search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onFocus={() => {
                searchFocusedRef.current = true;
                setToolbarHidden(false);
              }}
              onBlur={() => {
                searchFocusedRef.current = false;
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitSearch(searchText);
              }}
              placeholder={t("Search events")}
              aria-label={t("Search events")}
              className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-9 text-sm text-gray-700 outline-none transition placeholder:text-gray-400 focus:border-[#ffa217] focus:ring-2 focus:ring-[#ffa217]/30"
            />
            {searchText.length > 0 && (
              <button
                type="button"
                onClick={() => commitSearch("")}
                aria-label={t("Clear search")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 transition hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => setFilterModalOpen(true)}
            className={`relative flex shrink-0 items-center gap-1.5 rounded-xl border px-3.5 py-2.5 text-sm font-semibold transition ${
              hasActiveFilter
                ? "border-[#ffa217] text-[#ffa217]"
                : "border-gray-200 text-gray-600 hover:border-[#ffa217] hover:text-[#ffa217]"
            }`}
          >
            {hasActiveFilter ? (
              <SlidersHorizontal size={16} />
            ) : (
              <Plus size={16} />
            )}
            {hasActiveFilter ? t("Filters") : t("All")}
            {hasActiveFilter && (
              <span className="ml-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#ffa217] px-1 text-[11px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {isSearching && (
          <p className="mt-2 truncate text-xs text-gray-400">
            {t("Search results for")} &quot;{searchQuery}&quot;
          </p>
        )}

        {filtering && (
          <div className="mt-2 flex justify-center">
            <Loader2 size={16} className="animate-spin text-[#ffa217]" />
          </div>
        )}
      </div>

      {loading ? (
        <AdminListSkeleton count={3} />
      ) : error ? (
        <div className="rounded-2xl border border-gray-100 bg-white">
          <AdminEmptyState
            iconType="MaterialCommunityIcons"
            iconName="cloud-alert-outline"
            title={t("Something went wrong")}
            message={t("Pull to refresh to load events.")}
            actionLabel={t("Retry")}
            onAction={() => fetchPage(1, "refresh")}
          />
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-2xl border border-gray-100 bg-white">
          <AdminEmptyState
            iconType="Ionicons"
            iconName={isSearching ? "search-outline" : "calendar-outline"}
            title={
              isSearching
                ? t("No events match your search")
                : t("No events yet")
            }
            message={
              isSearching
                ? `${t("Search results for")} "${searchQuery}"`
                : t("Pull to refresh to see the latest events")
            }
            actionLabel={isSearching ? t("Clear search") : t("Refresh")}
            onAction={() =>
              isSearching ? commitSearch("") : fetchPage(1, "refresh")
            }
          />
        </div>
      ) : (
        <>
          {refreshing && (
            <div className="mb-3 flex justify-center">
              <Loader2 size={18} className="animate-spin text-[#ffa217]" />
            </div>
          )}

          {events.map((event) => (
            <StudentEventCard
              key={event.details.id}
              event={event}
              societyIds={societyIds}
              onSubscribedChange={handleSubscribedChange}
              onFollowChange={handleFollowChange}
            />
          ))}

          <div ref={sentinelRef} className="h-px" />
          {!hasMore && (
            <div className="py-5 text-center text-xs text-gray-400">
              {t("You're all caught up")}
            </div>
          )}
        </>
      )}

      {isSocietyMember && (
        <Link
          to="/student/events/new"
          aria-label={t("Create Event")}
          title={t("Create Event")}
          className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#ffa217] text-white shadow-lg transition hover:bg-[#f59200]"
        >
          <Plus size={24} />
        </Link>
      )}

      {filterModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setFilterModalOpen(false)}
          // Keep the page's pull-to-refresh gesture from starting inside the modal
          onTouchStart={(e) => e.stopPropagation()}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t("Filters")}
            className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">
                {t("Filters")}
              </h2>
              <button
                type="button"
                onClick={() => setFilterModalOpen(false)}
                aria-label={t("Close")}
                className="rounded-lg p-1 text-gray-400 transition hover:bg-gray-50 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            </div>

            <section className="mb-5">
              <span className="mb-2 block text-[11px] font-bold uppercase tracking-wide text-gray-400">
                {t("Filter by type")}
              </span>
              <div className="flex flex-wrap gap-2">
                <PostTypeChip
                  label={t("All")}
                  active={draftPostType === ALL}
                  onClick={() => setDraftPostType(ALL)}
                />
                {postTypes.map((value) => {
                  const labelKey = postTypeLabelKey(value);
                  return (
                    <PostTypeChip
                      key={value}
                      label={labelKey ? t(labelKey) : value}
                      active={draftPostType === value}
                      onClick={() => setDraftPostType(value)}
                    />
                  );
                })}
              </div>
            </section>

            <section className="mb-5">
              <span className="mb-2 block text-[11px] font-bold uppercase tracking-wide text-gray-400">
                {t("Filter by date")}
              </span>
              <div className="flex flex-wrap gap-2">
                {EVENT_DATE_PRESETS.map((preset) => (
                  <PostTypeChip
                    key={preset.value}
                    label={t(preset.label)}
                    active={draftDatePreset === preset.value}
                    onClick={() => setDraftDatePreset(preset.value)}
                  />
                ))}
              </div>
            </section>

            <section className="mb-5">
              <label className="block">
                <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-400">
                  {t("Filter by university")}
                </span>
                <span className="relative block">
                  <select
                    value={draftUniversity}
                    onChange={(e) => setDraftUniversity(e.target.value)}
                    className={FILTER_SELECT_CLASS}
                    aria-label={t("Filter by university")}
                  >
                    <option value={ALL}>{t("All")}</option>
                    {universityList.map((uni) => (
                      <option key={uni.value} value={uni.value}>
                        {t(uni.value)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                </span>
              </label>
            </section>

            <section className="mb-5">
              <label className="block">
                <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-400">
                  {t("Filter by society")}
                </span>
                <span className="relative block">
                  <select
                    value={draftSociety}
                    onChange={(e) => setDraftSociety(e.target.value)}
                    className={FILTER_SELECT_CLASS}
                    aria-label={t("Filter by society")}
                  >
                    <option value={ALL}>{t("All")}</option>
                    {scopedSocieties.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                </span>
              </label>
            </section>

            <section className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-400">
                  {t("Location")}
                </span>
                <input
                  type="text"
                  value={draftLocation}
                  onChange={(e) => setDraftLocation(e.target.value)}
                  placeholder={t("Any location")}
                  className={FILTER_SELECT_CLASS}
                  aria-label={t("Filter by location")}
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-400">
                  {t("Hashtag")}
                </span>
                <input
                  type="text"
                  value={draftHashtag}
                  onChange={(e) => setDraftHashtag(e.target.value)}
                  placeholder={t("Any hashtag")}
                  className={FILTER_SELECT_CLASS}
                  aria-label={t("Filter by hashtag")}
                />
              </label>
            </section>

            <label className="mb-5 flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={draftOpenOnly}
                onChange={(e) => setDraftOpenOnly(e.target.checked)}
                className="h-4 w-4 accent-[#ffa217]"
              />
              {t("Applications open only")}
            </label>

            <div className="sticky bottom-0 -mx-5 -mb-5 flex items-center justify-between gap-3 border-t border-gray-100 bg-white/95 px-5 py-3 backdrop-blur">
              <button
                type="button"
                onClick={() => {
                  setDraftPostType(ALL);
                  setDraftUniversity(ALL);
                  setDraftSociety(ALL);
                  setDraftDatePreset("any");
                  setDraftOpenOnly(false);
                  setDraftLocation("");
                  setDraftHashtag("");
                }}
                className="text-sm font-semibold text-[#ffa217] transition hover:text-[#f59200]"
              >
                {t("Clear all")}
              </button>
              <button
                type="button"
                onClick={applyFilters}
                className="rounded-xl bg-[#ffa217] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#f59200]"
              >
                {t("Apply filters")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentHomePage;
